import type { Project } from '../../../types/project'
import styles from './ProjectCard.module.css'

interface ProjectCardProps {
  project: Project
}

/* ── Icône GitHub SVG inline ─────────────────── */
const IconGitHub = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={styles.linkIcon}
  >
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z" />
  </svg>
)

/* ── Icône lien externe SVG inline ───────────── */
const IconExternalLink = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={styles.linkIcon}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

export function ProjectCard({ project }: ProjectCardProps) {
  const { name, image, imageAlt, description, stack, githubUrl, liveUrl } = project

  return (
    <article className={styles.card} aria-label={`Projet : ${name}`}>
      {/* ── Image ── */}
      <div className={styles.imageWrapper}>
        <img
          src={image}
          alt={imageAlt}
          className={styles.image}
          /* Lazy loading natif pour la performance */
          loading="lazy"
          decoding="async"
        />
        {/* Overlay au hover avec les liens */}
        <div className={styles.overlay} aria-hidden="true">
          <div className={styles.overlayLinks}>
            {githubUrl && (
              <a
                href={githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Code source de ${name} sur GitHub`}
                className={styles.overlayLink}
                /* Accessible malgré l'overlay : focus retiré du tabindex natif */
                tabIndex={-1}
              >
                <IconGitHub />
              </a>
            )}
            {liveUrl && (
              <a
                href={liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Voir ${name} en ligne`}
                className={styles.overlayLink}
                tabIndex={-1}
              >
                <IconExternalLink />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── Corps ── */}
      <div className={styles.body}>
        {/* Titre */}
        <h3 className={styles.name}>{name}</h3>

        {/* Description */}
        <p className={styles.description}>{description}</p>

        {/* Stack */}
        <ul className={styles.stack} aria-label="Technologies utilisées">
          {stack.map((tech) => (
            <li key={tech} className={styles.stackItem}>
              {tech}
            </li>
          ))}
        </ul>

        {/* ── Liens accessibles (hors overlay) ── */}
        <div className={styles.links}>
          {githubUrl && (
            <a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Code source de ${name} sur GitHub`}
              className={styles.link}
            >
              <IconGitHub />
              GitHub
            </a>
          )}
          {liveUrl && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Voir ${name} en ligne`}
              className={styles.link}
            >
              <IconExternalLink />
              Live
            </a>
          )}
        </div>
      </div>
    </article>
  )
}